import { type ChangeEvent, type ReactNode, useId, useState } from "react";

import {
  IconButton,
  Icons,
  InputLabel,
  type InputWrapperState,
  inputWrapperStyles,
} from "@fielmann-ag/prisma-components";

import { useI18n } from "@/i18n";
import { joinClassNames } from "@/lib/utils";

/**
 * Replicates Prisma's internal `inputBaseStyles` (not exported) so the native
 * password input stays typographically and spatially identical to `InputText`.
 * Kept in sync with `packages/components/src/components/input/utils/Input.styles.ts`
 * in the Prisma repo. When Prisma ships an `InputPassword`, this constant (and
 * this component) can be dropped in favour of theirs.
 */
const INPUT_BASE_CLASSES =
  "break-normal wrap-anywhere [line-height:1.2] text-[1.125rem] font-normal text-inherit placeholder:text-action-secondary focus-visible:outline-none min-h-3.5 rounded-l-[inherit]";

interface PasswordInputProps {
  label: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  showLabel?: boolean;
  state?: InputWrapperState;
  autoComplete?: string;
  /** Rendered below the field (e.g. PasswordRequirements). */
  helperText?: ReactNode;
  className?: string;
}

/**
 * Password field with show/hide masking.
 *
 * Prisma's `InputText` hardcodes `type="text"` and does not forward attributes
 * to the native `<input>`, so it cannot render a masked password.
 * This component is built from *exported* Prisma primitives
 * (`inputWrapperStyles`, `InputLabel`) plus a native `<input>` so it stays
 * visually identical to the rest of the Prisma input family while supporting
 * `type="password"`, `autoComplete`, and the show/hide eye toggle.
 *
 * The grid wrapper intentionally contains only the `<label>` and `<input>` —
 * the exact two children `InputText`'s grid expects — so the floating-label
 * overlap layout is identical. The eye button is a sibling of the grid wrapper
 * inside a `relative` parent (placing it inside the grid would create an extra
 * row and break the floating label).
 *
 * Encapsulated behind a Prisma-`InputText`-shaped API so swapping to a future
 * Prisma `InputPassword` is a contained, internal change with no new deps.
 */
export function PasswordInput({
  label,
  name,
  value,
  onChange,
  onBlur,
  showLabel = true,
  state,
  autoComplete,
  helperText,
  className,
}: PasswordInputProps) {
  const { messages } = useI18n();
  const inputId = useId();
  const helperTextId = useId();
  const [isFocused, setIsFocused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const hasValue = value !== "";

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    onChange(event.target.value);
  }

  return (
    <div className={className}>
      {/* Keep the field controls below the sticky header and tabs while scrolling. */}
      <div className="relative isolate">
        {/* Input wrapper and floating label. */}
        <div
          className={joinClassNames(
            inputWrapperStyles(state),
            "[&>label,&>input]:col-span-full [&>label,&>input]:row-span-full",
          )}
        >
          {showLabel && (
            <InputLabel
              label={label}
              isFloating={isFocused || hasValue}
              isFocused={isFocused}
              htmlFor={inputId}
              className="pr-1"
            />
          )}
          <input
            id={inputId}
            name={name}
            type={isVisible ? "text" : "password"}
            value={value}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
            autoComplete={autoComplete}
            required
            aria-invalid={state === "error"}
            aria-describedby={helperText ? helperTextId : undefined}
            // pt mirrors InputBase's `showLabel && "pt-[1.1875rem]"` so typed
            // text sits below the floating label, matching InputText exactly.
            // pr-3 reserves room for the 40px eye button at right-0.5.
            className={joinClassNames(
              INPUT_BASE_CLASSES,
              showLabel && "pt-[1.1875rem]",
              "pl-1 pr-3",
            )}
          />
        </div>
        {/* Masking toggle stays outside the grid to preserve the floating-label layout. */}
        <IconButton
          label={
            isVisible
              ? messages.shared.password.hide
              : messages.shared.password.show
          }
          variant="plain"
          size={50}
          icon={
            isVisible ? (
              <Icons.Medium.EyeClosed aria-hidden="true" />
            ) : (
              <Icons.Medium.Eye aria-hidden="true" />
            )
          }
          onPress={() => setIsVisible((previous) => !previous)}
          // Figma's plain .InputButton uses the compact 4px radius.
          className="absolute right-0.5 top-1/2 z-20 -translate-y-1/2 !rounded-25"
        />
        {/* Error outline is separate from the input wrapper to match Prisma styling. */}
        {state === "error" ? (
          <span
            data-password-input-error-outline
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-30 rounded-75 border border-error-secondary"
          />
        ) : null}
      </div>
      {helperText ? (
        <div id={helperTextId} className="mt-0.5">
          {helperText}
        </div>
      ) : null}
    </div>
  );
}
